export type ForecastCategoryStatus = 'ok' | 'warning' | 'at_risk' | 'no_budget';

export interface ForecastCategory {
  categorie: string;
  depense_mois: number;
  budget_limite: number | null;
  projection_fin_mois: number;
  statut: ForecastCategoryStatus;
}

export interface MonthForecast {
  jour_actuel: number;
  jours_restants: number;
  jours_du_mois: number;
  depense_mois: number;
  revenu_mois: number;
  projection: number;
  solde_projete: number;
  categories: ForecastCategory[];
}

export interface PrevisionFinMoisContext {
  jour_actuel: number;
  jours_restants: number;
  depense_mois: number;
  projection: number;
  solde_projete: number;
  categories_a_risque: Array<{
    categorie: string;
    projection: number;
    budget: number;
  }>;
}

export function normalizeMonthForecast(raw: unknown): MonthForecast | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const categoriesRaw = Array.isArray(o.categories) ? o.categories : [];
  return {
    jour_actuel: Number(o.jour_actuel ?? 0),
    jours_restants: Number(o.jours_restants ?? 0),
    jours_du_mois: Number(o.jours_du_mois ?? 0),
    depense_mois: Number(o.depense_mois ?? 0),
    revenu_mois: Number(o.revenu_mois ?? 0),
    projection: Number(o.projection ?? 0),
    solde_projete: Number(o.solde_projete ?? 0),
    categories: categoriesRaw.map((c) => {
      const row = (c || {}) as Record<string, unknown>;
      const statut = String(row.statut || 'no_budget') as ForecastCategoryStatus;
      return {
        categorie: String(row.categorie || 'Sans catégorie'),
        depense_mois: Number(row.depense_mois ?? 0),
        budget_limite:
          row.budget_limite == null || row.budget_limite === ''
            ? null
            : Number(row.budget_limite),
        projection_fin_mois: Number(row.projection_fin_mois ?? 0),
        statut: ['ok', 'warning', 'at_risk', 'no_budget'].includes(statut)
          ? statut
          : 'no_budget',
      };
    }),
  };
}

export function forecastToAiContext(f: MonthForecast): PrevisionFinMoisContext {
  return {
    jour_actuel: f.jour_actuel,
    jours_restants: f.jours_restants,
    depense_mois: f.depense_mois,
    projection: f.projection,
    solde_projete: f.solde_projete,
    categories_a_risque: f.categories
      .filter((c) => c.statut === 'at_risk' || c.statut === 'warning')
      .filter((c) => c.budget_limite != null)
      .map((c) => ({
        categorie: c.categorie,
        projection: c.projection_fin_mois,
        budget: Number(c.budget_limite),
      })),
  };
}

function yyyyMm(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function daysInMonth(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
}

/**
 * Prévision pour le contexte IA — calcul local (pas d'appel RPC).
 * Les maps utilisent des clés yyyy-MM.
 */
export function buildPrevisionFinMoisFromAggregates(input: {
  now?: Date;
  depensesByMonth: Record<string, number>;
  revenusByMonth: Record<string, number>;
  /** catégorie → yyyy-MM → montant */
  depensesParCategorie: Record<string, Record<string, number>>;
  budgets: Array<{ categorie: string; montant_alloue: number }>;
}): PrevisionFinMoisContext {
  const now = input.now ?? new Date();
  const jour_actuel = now.getDate();
  const jours_du_mois = daysInMonth(now);
  const jours_restants = Math.max(jours_du_mois - jour_actuel, 0);
  const currentKey = yyyyMm(now);

  const depense_mois = Math.round((input.depensesByMonth[currentKey] || 0) * 100) / 100;
  const revenu_mois = Math.round((input.revenusByMonth[currentKey] || 0) * 100) / 100;

  let prevTotal = 0;
  let prevDays = 0;
  for (let i = 1; i <= 3; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    prevTotal += input.depensesByMonth[yyyyMm(d)] || 0;
    prevDays += daysInMonth(d);
  }
  const moyJour = prevDays > 0 ? prevTotal / prevDays : 0;
  const projection = Math.round((depense_mois + moyJour * jours_restants) * 100) / 100;
  const solde_projete = Math.round((revenu_mois - projection) * 100) / 100;

  const categories_a_risque: PrevisionFinMoisContext['categories_a_risque'] = [];
  for (const [cat, months] of Object.entries(input.depensesParCategorie)) {
    const budget = input.budgets.find((b) => b.categorie === cat)?.montant_alloue;
    if (budget == null || budget <= 0) continue;

    let catPrev = 0;
    for (let i = 1; i <= 3; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      catPrev += Number(months[yyyyMm(d)] || 0);
    }
    const catCurrent = Number(months[currentKey] || 0);
    const catMoyJour = prevDays > 0 ? catPrev / prevDays : 0;
    const catProjection =
      Math.round((catCurrent + catMoyJour * jours_restants) * 100) / 100;

    if (catProjection > budget * 0.9) {
      categories_a_risque.push({
        categorie: cat,
        projection: catProjection,
        budget,
      });
    }
  }

  categories_a_risque.sort((a, b) => b.projection - a.projection);

  return {
    jour_actuel,
    jours_restants,
    depense_mois,
    projection,
    solde_projete,
    categories_a_risque: categories_a_risque.slice(0, 8),
  };
}
